/* =============================================================
   NAELLIA — WISHLIST (Shopify Markets-aware pricing)
   localStorage remembers only wishlist selections / product details.
   Displayed prices always come from Shopify's locale-aware Product Ajax
   API in the visitor's ACTIVE presentment currency. A historical saved
   price is never used: it might belong to another market or be outdated.
   ============================================================= */
(function () {
  'use strict';

  var KEY = 'naellia:wishlist';
  var MAX_PRICE_REQUESTS = 4;
  var FOCUS_REFRESH_MS = 5 * 60 * 1000;
  var $ = function (selector, root) { return (root || document).querySelector(selector); };
  var $$ = function (selector, root) { return Array.prototype.slice.call((root || document).querySelectorAll(selector)); };
  var prices = Object.create(null); // Memory only. Never persist market-specific prices.
  var waiting = Object.create(null);
  var queue = [];
  var activeRequests = 0;
  var lastSyncAt = 0;

  function esc(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }
  function validItem(item) {
    return !!(item && typeof item === 'object' && typeof item.handle === 'string' && item.handle.length); 
  }
  function read() {
    try {
      var items = JSON.parse(localStorage.getItem(KEY) || '[]');
      return Array.isArray(items) ? items.filter(validItem) : [];
    } catch (error) { return []; }
  }
  function save(items, announce) {
    try { localStorage.setItem(KEY, JSON.stringify(items)); } catch (error) { /* storage may be unavailable */ }
    if (announce) document.dispatchEvent(new CustomEvent('naellia:wishlist:change'));
  }
  function write(items) { save(items, true); }
  function has(handle) { return read().some(function (item) { return item.handle === handle; }); }

  function shopifyRoot() {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return root.slice(-1) === '/' ? root : root + '/';
  }
  function productUrl(handle) {
    // Saved URLs may contain a DIFFERENT market's locale prefix.
    return shopifyRoot() + 'products/' + encodeURIComponent(handle);
  }
  function productJsonUrl(handle) { return productUrl(handle) + '.js'; }
  function currentCurrency() {
    var market = window.NaelliaMarket || (window.Naellia && window.Naellia.market) || {};
    return String(market.currency || '').toUpperCase();
  }
  function money(cents) {
    if (window.NaelliaMarket && typeof window.NaelliaMarket.formatMoney === 'function') {
      return window.NaelliaMarket.formatMoney(cents);
    }
    var currency = currentCurrency();
    var amount = (Number(cents) || 0) / 100;
    try { return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: currency }).format(amount); }
    catch (error) { return currency + ' ' + amount.toFixed(2); }
  }
  function itemFrom(button) {
    return {
      handle: button.getAttribute('data-wish-handle') || '',
      title: button.getAttribute('data-wish-title') || '',
      url: button.getAttribute('data-wish-url') || '',
      image: button.getAttribute('data-wish-image') || ''
      // Deliberately NO data-wish-price: it is a historical formatted string.
    };
  }
  function toggle(item) {
    var items = read();
    var index = items.findIndex(function (saved) { return saved.handle === item.handle; });
    if (index > -1) items.splice(index, 1);
    else if (validItem(item)) items.push(item);
    write(items);
    if (index === -1 && validItem(item)) updatePrices([item.handle], false);
    return index === -1;
  }
  function remove(handle) { write(read().filter(function (item) { return item.handle !== handle; })); }
  window.NaelliaWishlist = { read: read, remove: remove, toggle: toggle, has: has };

  function validCents(value) {
    return value !== null && value !== undefined && value !== '' && Number.isFinite(Number(value)) && Number(value) >= 0;
  }
  function getPrice(product) {
    var amount = validCents(product.price_min) ? product.price_min : product.price;
    if (!validCents(amount) && Array.isArray(product.variants)) {
      var variantPrices = product.variants.map(function (variant) { return variant.price; }).filter(validCents);
      if (variantPrices.length) amount = Math.min.apply(null, variantPrices);
    }
    if (!validCents(amount)) return null;
    var compare = validCents(product.compare_at_price) ? Number(product.compare_at_price) : null;
    var varies = product.price_varies === true;
    if (product.price_varies == null && Array.isArray(product.variants)) {
      varies = product.variants.some(function (variant) { return validCents(variant.price) && Number(variant.price) !== Number(amount); });
    }
    // For variable-priced products, the compare-at might belong to another
    // variant: don't imply a fictitious discount on the minimum price.
    if (varies || compare === null || compare <= Number(amount)) compare = null;
    return { status: 'ready', cents: Number(amount), compareAt: compare, varies: varies,
      currency: currentCurrency(), updatedAt: Date.now() };
  }

  function priceHTML(handle) {
    var state = prices[handle];
    if (!state || state.status === 'loading' || state.currency !== currentCurrency()) {
      return '<span class="wl-price-pending" aria-live="polite">Checking price…</span>';
    }
    if (state.status === 'unavailable') return '<span class="wl-price-unavailable">Unavailable in this market</span>';
    if (state.status !== 'ready') return '<span class="wl-price-unavailable">View current price</span>';
    var visible = (state.varies ? 'From ' : '') + money(state.cents);
    return '<span class="wl-price-current">' + esc(visible) + '</span>' +
      (state.compareAt !== null ? ' <s class="wl-price-compare">' + esc(money(state.compareAt)) + '</s>' : '');
  }
  function lineHTML(item) {
    var url = productUrl(item.handle);
    return '<div class="cart-line" data-wl-line="' + esc(item.handle) + '">' +
      '<a class="thumb" href="' + esc(url) + '">' +
      (item.image ? '<img src="' + esc(item.image) + '" alt="' + esc(item.title) + '" width="70" height="93">' : '') + '</a>' +
      '<div class="meta"><h4><a href="' + esc(url) + '">' + esc(item.title) + '</a></h4>' +
      '<div class="price">' + priceHTML(item.handle) + '</div>' +
      '<a class="remove" href="#" data-wl-remove="' + esc(item.handle) + '">Remove</a></div>' +
      '<a class="up-add" href="' + esc(url) + '" style="align-self:center">View</a></div>';
  }
  function renderDrawer(items) {
    var target = $('#wlItems'); if (!target) return;
    var count = $('#wlCount'); if (count) count.textContent = items.length;
    target.innerHTML = items.length ? items.map(lineHTML).join('') :
      '<div class="cart-empty"><div class="big">Your wishlist is empty</div>Tap the heart on any piece to save it here.</div>';
  }
  function renderPage(items) {
    var target = $('#wishlistGrid');
    var empty = $('#wishlistEmpty');
    if (!target) return;
    if (!items.length) {
      target.hidden = true;
      if (empty) empty.hidden = false;
      return;
    }
    if (empty) empty.hidden = true;
    target.hidden = false;
    target.innerHTML = items.map(function (item) {
      var url = productUrl(item.handle);
      return '<div class="prod-card" data-wl-line="' + esc(item.handle) + '">' +
        '<a class="prod-media" href="' + esc(url) + '" style="display:block">' +
        (item.image ? '<img src="' + esc(item.image) + '" alt="' + esc(item.title) + '" width="350" height="478">' : '') + '</a>' +
        '<button class="wish on" type="button" data-wl-remove="' + esc(item.handle) + '" aria-label="Remove">♥</button>' +
        '<div class="prod-info"><h3><a href="' + esc(url) + '">' + esc(item.title) + '</a></h3>' +
        '<div class="price">' + priceHTML(item.handle) + '</div></div></div>';
    }).join('');
  }
  function refresh() {
    var items = read();
    $$('[data-wish]').forEach(function (button) {
      var handle = button.getAttribute('data-wish-handle');
      var selected = !!handle && items.some(function (item) { return item.handle === handle; });
      button.classList.toggle('on', selected);
      button.setAttribute('aria-pressed', String(selected));
      if (button.classList.contains('wish') && !button.querySelector('svg')) button.textContent = selected ? '♥' : '♡';
    });
    $$('[data-wishlist-count]').forEach(function (node) {
      node.textContent = items.length;
      node.style.display = items.length ? '' : 'none';
    });
    renderDrawer(items);
    renderPage(items);
  }

  function syncProductDetails(handle, product) {
    var items = read();
    var changed = false;
    items.forEach(function (item) {
      if (item.handle !== handle) return;
      if (product.title && item.title !== product.title) { item.title = product.title; changed = true; }
      var newImage = typeof product.featured_image === 'string' ? product.featured_image : '';
      if (newImage && newImage !== item.image) { item.image = newImage; changed = true; }
      // Old saved market-specific URLs must no longer be used.
      var newUrl = productUrl(handle);
      if (item.url !== newUrl) { item.url = newUrl; changed = true; }
      // Strip legacy snapshot; a formatted price cannot be reused safely.
      if (Object.prototype.hasOwnProperty.call(item, 'price')) { delete item.price; changed = true; }
      if (Object.prototype.hasOwnProperty.call(item, 'checkedAt')) { delete item.checkedAt; changed = true; }
    });
    if (changed) save(items, false);
  }
  function runQueue() {
    while (activeRequests < MAX_PRICE_REQUESTS && queue.length) {
      (function (job) {
        activeRequests++;
        fetch(productJsonUrl(job.handle), { credentials: 'same-origin', headers: { Accept: 'application/json' }, cache: 'no-store' })
          .then(function (response) {
            if (response.status === 404 || response.status === 410) {
              prices[job.handle] = { status: 'unavailable', currency: currentCurrency(), updatedAt: Date.now() };
              return null;
            }
            if (!response.ok) throw new Error('Product price could not be retrieved');
            return response.json();
          })
          .then(function (product) {
            if (!product) return;
            var result = getPrice(product);
            if (!result) throw new Error('Product does not contain a valid price');
            prices[job.handle] = result;
            syncProductDetails(job.handle, product);
          })
          .catch(function () {
            // Never show an old AUD/NZD/GBP amount as if it were current.
            prices[job.handle] = { status: 'error', currency: currentCurrency(), updatedAt: Date.now() };
          })
          .then(function () {
            delete waiting[job.handle];
            activeRequests--;
            refresh();
            job.resolve();
            runQueue();
          });
      })(queue.shift());
    }
  }
  function updatePrices(handles, force) {
    var items = handles || read().map(function (item) { return item.handle; });
    if (!items.length) return;
    var now = Date.now();
    lastSyncAt = now;
    items.forEach(function (handle) {
      if (waiting[handle]) return;
      var previous = prices[handle];
      if (!force && previous && previous.currency === currentCurrency() &&
        previous.status === 'ready' && now - previous.updatedAt < FOCUS_REFRESH_MS) return;
      prices[handle] = { status: 'loading', currency: currentCurrency() };
      waiting[handle] = true;
      queue.push({ handle: handle, resolve: function () {} });
    });
    refresh();
    runQueue();
  }

  function openWL() {
    var drawer = $('#wishlistDrawer');
    var overlay = $('#wlOverlay');
    if (!drawer) return;
    drawer.classList.add('open');
    drawer.setAttribute('aria-hidden', 'false');
    if (overlay) overlay.classList.add('open');
    document.body.style.overflow = 'hidden';
    // Always recheck when the visitor opens the drawer, including after a
    // Markets country switch or a price change while the tab stays open.
    updatePrices(null, true);
  }
  function closeWL() {
    var drawer = $('#wishlistDrawer');
    var overlay = $('#wlOverlay');
    if (drawer) { drawer.classList.remove('open'); drawer.setAttribute('aria-hidden', 'true'); }
    if (overlay) overlay.classList.remove('open');
    document.body.style.overflow = '';
  }
  window.NaelliaWishlist.open = openWL;
  window.NaelliaWishlist.close = closeWL;

  document.addEventListener('click', function (event) {
    var heart = event.target.closest('[data-wish]');
    if (heart && heart.hasAttribute('data-wish-handle')) {
      event.preventDefault();
      event.stopPropagation();
      toggle(itemFrom(heart));
      return;
    }
    if (event.target.closest('[data-wishlist-open]')) {
      event.preventDefault();
      openWL();
      return;
    }
    if (event.target.closest('[data-wishlist-close]') || event.target.id === 'wlOverlay') {
      closeWL();
      return;
    }
    var removeButton = event.target.closest('[data-wl-remove]');
    if (removeButton) {
      event.preventDefault();
      remove(removeButton.getAttribute('data-wl-remove'));
    }
  });
  document.addEventListener('naellia:wishlist:change', refresh);

  function initWishlist() {
    refresh();
    updatePrices(null, true);
  }
  if (document.readyState !== 'loading') initWishlist();
  else document.addEventListener('DOMContentLoaded', initWishlist);

  window.addEventListener('pageshow', function (event) {
    if (event.persisted) updatePrices(null, true);
  });
  document.addEventListener('visibilitychange', function () {
    if (document.hidden) return;
    refresh();
    if (Date.now() - lastSyncAt >= FOCUS_REFRESH_MS) updatePrices(null, true);
  });
  window.addEventListener('focus', function () {
    if (Date.now() - lastSyncAt >= FOCUS_REFRESH_MS) updatePrices(null, true);
  });
  window.addEventListener('storage', function (event) {
    if (event.key !== KEY) return;
    refresh();
    updatePrices(null, false);
  });
})();

/* =============================================================
   NAELLIA — CARD COLOUR HOVER (NA-KD): hover a colour dot →
   card image swaps to that colour + a name tooltip appears.
   Delegated so it also works on lazy-loaded recommendation cards.
   ============================================================= */
(function () {
  'use strict';

  document.addEventListener('mouseover', function (e) {
    var dot = e.target.closest ? e.target.closest('.cc-dot') : null;
    if (!dot) return;

    var card = dot.closest('.prod-card');
    if (!card) return;

    var main = card.querySelector('.pm-main');

    if (main) {
      if (!main.getAttribute('data-cc-orig')) {
        main.setAttribute('data-cc-orig', main.getAttribute('src') || '');

        if (main.getAttribute('srcset')) {
          main.setAttribute('data-cc-origset', main.getAttribute('srcset'));
        }
      }

      var img = dot.getAttribute('data-cc-img');

      if (img) {
        main.setAttribute('src', img);
        main.removeAttribute('srcset');
        card.classList.add('cc-active');
      }
    }

    var tip = card.querySelector('[data-cc-tip]');

    if (tip) {
      tip.textContent = dot.getAttribute('data-cc-label') || '';
      tip.classList.add('on');
    }
  });

  document.addEventListener('mouseout', function (e) {
    var dot = e.target.closest ? e.target.closest('.cc-dot') : null;
    if (!dot) return;

    var row = dot.closest('.card-colors');
    var to = e.relatedTarget && e.relatedTarget.closest ? e.relatedTarget.closest('.card-colors') : null;

    if (to === row) return;

    var card = dot.closest('.prod-card');
    if (!card) return;

    var main = card.querySelector('.pm-main');

    if (main && main.getAttribute('data-cc-orig') !== null) {
      var o = main.getAttribute('data-cc-orig');

      if (o) {
        main.setAttribute('src', o);
      }

      var os = main.getAttribute('data-cc-origset');

      if (os) {
        main.setAttribute('srcset', os);
      }
    }

    card.classList.remove('cc-active');

    var tip = card.querySelector('[data-cc-tip]');

    if (tip) {
      tip.classList.remove('on');
    }
  });
})();